import { DecimalFormatOptions, IntegerFormatOptions } from 'interfaces-lib';
import {FieldMetaTypeGuards} from '../interfaces/FieldMetaTypeGuards';
import {
  AIASSIST_FIELDS_ENTITY_NAME,
  CONDITIONAL_FIELDS_ENTITY_NAME,
  EntityTypeLabelPair,
  IFieldMeta,
  IMAGE_FIELDS_ENTITY_NAME,
  SCRIPT_FIELDS_ENTITY_NAME,
} from '../interfaces/imetadata';
import { ILogProvider } from '../services/logging.service';
import { Condition } from './condition';
import { FieldPathUtils } from './fieldpath-utils';
import {
  AiAssistFieldOptions,
  DateFormatOptions,
  ImageFieldOptions,
  SortOption,
  StringFormatOptions,
} from './fieldconfig';

/**
 * Represents a field and the context of how it is to be added to Word
 */
export class FieldContext implements ILogProvider {
  readonly namespace: string;
  readonly entity: EntityTypeLabelPair;
  readonly field: IFieldMeta;
  conditions?: Condition[];
  dateFormatOptions?: DateFormatOptions;
  defaultEmptyValue?: string;
  integerFormat?: IntegerFormatOptions;
  decimalFormat?: DecimalFormatOptions;
  sortOption?: SortOption; // only applies to iterable fields
  limit?: number;
  stringFormat?: StringFormatOptions;
  useDisplayValue?: boolean; // in BH there is a value -> "display value" mapping - by default we use value
  imageFieldConfig?: ImageFieldOptions;
  aiAssistFieldConfig?: AiAssistFieldOptions;

  /**
   * Standard constructor
   * @param namespace the namespace the field belongs to (e.g. bullhorn, invenias, etc.)
   * @param entity the entity the field belongs to
   * @param field the field meta information
   * @param [conditions] a conditions clause associated with the field
   * @param [dateFormatOptions] the new format to be used for a date field
   * @param [integerFormat] the format to be used for an integer field
   * @param [decimalFormat] the format to be used for a decimal field
   * @param [sortOption] the sort options to be used if the field is iterable
   * @param [limit] the option to limit the number of items to be displayed if the field is iterable
   * @param [defaultEmptyValue] the value to be used when the datasource does not have a value
   * @param [stringFormat] the format to be used for a string field
   * @param [useDisplayValue] whether to use the display value rather than the value
   * @param [imageFieldConfig] the image field configuration
   * @param [aiAssistFieldConfig] the aiAssist field configuration
   */
  constructor(
    namespace: string,
    entity: EntityTypeLabelPair,
    field: IFieldMeta,
    conditions?: Condition[],
    dateFormatOptions?: DateFormatOptions,
    integerFormat?: IntegerFormatOptions,
    decimalFormat?: DecimalFormatOptions,
    sortOption?: SortOption,
    limit?: number,
    defaultEmptyValue?: string,
    stringFormat?: StringFormatOptions,
    useDisplayValue?: boolean,
    imageFieldConfig?: ImageFieldOptions,
    aiAssistFieldConfig?: AiAssistFieldOptions
  ) {
    this.namespace = namespace;
    this.entity = entity;
    this.field = field;
    this.conditions = conditions;
    this.dateFormatOptions = dateFormatOptions;
    this.integerFormat = integerFormat;
    this.decimalFormat = decimalFormat;
    this.sortOption = sortOption;
    this.limit = limit;
    this.defaultEmptyValue = defaultEmptyValue;
    this.stringFormat = stringFormat;
    this.useDisplayValue = useDisplayValue;
    this.imageFieldConfig = imageFieldConfig;
    this.aiAssistFieldConfig = aiAssistFieldConfig;
  }

  /**
   * Whether or not the field is a complex type that can potentially have repeating child fields (i.e. a to-many)
   */
  get isIterable(): boolean {
    return this.field.type === 'to-many';
  }

  /**
   * Whether or not the field is the child of an iterable field (i.e. child of a to-many)
   */
  get isIterableItem(): boolean {
    // return this.field.parent !== undefined;
    return this.field.parent?.type === 'to-many';
  }

  /**
   * Whether or not the field is a complex type that represents the parent of child fields but is not iterable (e.g. to-one, composite)
   */
  get isContainer(): boolean {
    return this.field.type === 'to-one' || this.field.type === 'composite';
  }

  get isPickerField(): boolean {
    return FieldMetaTypeGuards.isBullhorn(this.field) && this.field.isPickerField === true;
  }

  /**
   * Whether or not the field has a condition associated with it
   */
  get hasCondition(): boolean {
    return (this.conditions && this.conditions.length > 0) as boolean;
  }

  /**
   * Gets the leaf item of a field path (e.g. candidates from invenias.assignmentsListItem.candidates)
   */
  get leaf(): string {
    return FieldPathUtils.leaf(this.field.path);
  }

  /**
   * Whether or not the field path is an entity field (i.e. <entity>.<field_name>)
   */
  get isEntityField(): boolean {
    return this.field.path === `${this.entity.entity}.${this.leaf}`;
    // return this.field.parent === undefined
  }

  /**
   * Whether this is a script field (i.e. custom field linked to code)
   */
  get isScriptField(): boolean {
    return this.entity.entity === SCRIPT_FIELDS_ENTITY_NAME;
  }

  /**
   * Whether this is an image field
   */
  get isImageField(): boolean {
    return this.entity.entity === IMAGE_FIELDS_ENTITY_NAME;
  }

  /**
   * Whether this is a conditional field
   */
  get isConditionalField(): boolean {
    return this.entity.entity === CONDITIONAL_FIELDS_ENTITY_NAME;
  }

  /**
   * Whether this is an AI Assist field
   */
  get isAiAssistField(): boolean {
    return this.entity.entity === AIASSIST_FIELDS_ENTITY_NAME;
  }

  /**
   * Gets the field path without the leaf
   */
  tailless(): string {
    return this.field.path.split('.').slice(0, -1).join('.');
  }

  /**
   * Provides a loggable version of the field context
   * Note: removes recursive properties of field
   */
  toLog(): any {
    return {
      ...this,
      field: {
        path: this.field.path,
        type: this.field.type,
        label: this.field.label,
      },
      entity: {
        entity: this.entity.entity,
        label: this.entity.label,
      },
    };
  }
}
